
import { defineComponent, PropType } from 'vue';
import { mapState } from 'pinia';
import type { SearchBoxLoungesComponent } from '@white-label-types/lounges-component';
import { useSearchResultsStore } from '@white-label-store/search-results';
import SearchBoxLounges from '../search-box-lounges/search-box-lounges.vue';
import SearchLoungesInfoPanel from '../search-lounges-info-panel/search-lounges-info-panel.vue';
import { ModalCenter } from 'ui-shared-components';
import { useScreenSize } from '@white-label-helper/shared-composables';
import { FEATURE_FLAGS } from '@white-label-configuration/constants';

export default defineComponent({
  name: 'SearchLoungesWidget',
  components: {
    IconButton: () => import('@white-label-icon/icon-button'),
    SearchBoxLounges,
    SearchInfoPanel: SearchLoungesInfoPanel,
    ModalCenter,
  },
  props: {
    panelParams: {
      type: Object,
      default() {
        return {};
      },
    },
    customErrorMessages: {
      type: Object,
      required: false,
      default: () => {},
    },
    apiRequestRunning: {
      type: Boolean,
      default: false,
    },
    minEntryDate: {
      type: [String, Date] as PropType<string | Date>,
      default: () => new Date(),
    },
    enableExtrasProductInput: {
      type: Boolean,
      default: false,
    },
    isEditable: {
      type: Boolean,
      default: true,
    },
    showAgeRanges: {
      type: Boolean,
      default: true,
    },
    searchCriteriaConfig: {
      type: Object,
      default: null
    },
    noDateFields: {
      type: Boolean,
      default: false,
    },
    showBackdropOnElementFocus: {
      type: Boolean,
      default: false,
    },
  },
  setup() {
    const { isMobileResolution: checkMobile } = useScreenSize();
    return { checkMobile };
  },
  data() {
    return {
      editMode: false,
      editModeForDesktop: false,
      showDesktopBackdrop: false,
      isUpdateButtonEnabled: false,
      searchCriteria: {},
      disabledParams: {
        airport: '',
        entryDate: '',
        entryTime: {
          name: '',
          value: '',
        },
        exitDate: '',
        exitTime: null,
        terminal: '',
        airline: '',
        customers: {
          adult: 0,
          child: 0,
          infant: 0,
          senior: 0,
        },
        discount: '',
        swapped: false,
        baggage_type: '',
        extrasProduct: '',
      },
    };
  },
  computed: {
    ...mapState(useSearchResultsStore, ['searchErrorApi']),
    enabledAetherImprovements() {
      return this.$launchDarkly.variation(FEATURE_FLAGS.PREM_636_AETHER_NEW_SEARCH_FORM);
    },
    isMobileResolution() {
      if (typeof this.checkMobile === 'function') {
         return this.checkMobile();
      }
      return window.innerWidth < 1024;
    },
  },
  watch: {
    searchErrorApi(value) {
      if (value && this.isMobileResolution) this.openEditModal();
    },
  },

  mounted() {
  window.addEventListener('popstate', (event) => {
    if (!event.state?.editSearch) {
      this.closeEditModal();
    }
  });
},

  methods: {
    submitForm(data: any) {
      this.$nextTick(() => {
        this.$refs.form &&
          (this.$refs.form as SearchBoxLoungesComponent).submitForm(data);
      });
    },
    setSearchCriteria(data: any) {
      (this.$refs.form as SearchBoxLoungesComponent).setSearchCriteria(data);
    },
    updateCustomers(data: any) {
      (this.$refs.form as SearchBoxLoungesComponent).updateCustomers(data);
    },
    pushGTM() {
      (this.$refs.form as SearchBoxLoungesComponent).pushGTM();
    },
    openEditModal() {
      this.editMode = true;
      if (this.isMobileResolution) {
        history.pushState({ editSearch: this.editMode }, "Popup", "#edit-search");
      }
    },
    closeEditModal() {
      this.editMode = false;
      if (history.state?.editSearch) {
        history.back();
      }
    },
    submit(data: any) {
      this.showDesktopBackdrop = false
      if (!data.error) {
        this.disabledParams = { ...data.form };
        this.searchCriteria = data.searchCriteria;
        this.closeEditModal();
        this.isUpdateButtonEnabled = false;
        localStorage.setItem('searchCriteriaSerialized', JSON.stringify(data.searchCriteria) ?? '{}');
        this.$emit('submit', data);
      } else {
        this.openEditModal();
        if (!Object.keys(this.searchCriteria).length) {
          this.searchCriteria = data.searchCriteria;
        }
      }
    },
    submitFromModal(data: any) {
      if ((data || {}).error) return;
      this.submit(data);
      this.setSearchCriteria((data || {}).searchCriteria);
      this.editModeForDesktop = false;
    },
    focus() {
      this.openEditModal();
      (this.$refs.form as SearchBoxLoungesComponent).focus();
    },
    openGuestSelector() {
      this.openEditModal();
      this.$refs.form.openGuestSelector();
    },
    setFocusOnElement() {
      this.showBackdropOnElementFocus && (this.showDesktopBackdrop = true);
    },
    removeFocus() {
      this.showBackdropOnElementFocus && (this.showDesktopBackdrop = false);
    },

    // for accessibility when focus is on the edit form
    // closes edit form when using keyboard to navigate outside of the modal
    handleFocusOut(event: FocusEvent) {
      const modal: EventTarget | null = event.currentTarget;
      const focusedModalElement = event.relatedTarget;

      if (!focusedModalElement) {
        return;
      }

      if(modal.contains(focusedModalElement)) {
        return;
      }

      if(focusedModalElement.closest('[class*="dropdown"]')) {
        return;
      }

      this.closeEditModal();
    },
    // for accessibility to escape focus trap on searchbox form
    handleEscFromChild() {
      if (!this.isMobileResolution) {
        this.$emit('escape-widget-focus');
      }
      this.removeFocus();
    },
    // for conditionally enabling v-focus-trap on searchbox if values were changed
    updateButtonStateHandler(isNowEnabled: boolean) {
      this.isUpdateButtonEnabled = isNowEnabled;
    },
  }
});
